import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, RefreshCw, CheckCircle, XCircle, Link as LinkIcon, Code, MoreVertical, Eye } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { formatDateTimeDisplay } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';

import { API, getAuthHeader } from '../App';
import { formatErrorMessage } from '../utils/formatError';

const Integrations = () => {
  const { isMobile, isTablet } = useDeviceDetection();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'REST_API',
    description: '',
    base_url: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    webhook_url: '',
    config: {},
    is_active: true
  });

  // getAuthHeader function now imported from App.js

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`, { headers: getAuthHeader() });
      setIntegrations(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch integrations');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/integrations`, formData, { headers: getAuthHeader() });
      toast.success('Integration added successfully');
      setDialogOpen(false);
      resetForm();
      fetchIntegrations();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add integration'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      try {
        await axios.delete(`${API}/integrations/${id}`, { headers: getAuthHeader() });
        toast.success('Integration deleted successfully');
        fetchIntegrations();
      } catch (error) {
        toast.error('Failed to delete integration');
      }
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const response = await axios.post(`${API}/integrations/${id}/test`, {}, { headers: getAuthHeader() });
      if (response.data.status === 'success') {
        toast.success(response.data.message);
      } else if (response.data.status === 'error') {
        toast.error(response.data.message);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to test integration');
    } finally {
      setTestingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'REST_API',
      description: '',
      base_url: '',
      api_key: '',
      api_secret: '',
      access_token: '',
      webhook_url: '',
      config: {},
      is_active: true
    });
  };

  const integrationTemplates = {
    'GoFleet': {
      name: 'GoFleet',
      type: 'REST_API',
      description: 'Real-time vehicle tracking and fleet management',
      base_url: 'https://api.gofleet.com/v1',
      placeholder_key: 'Enter your GoFleet API key'
    },
    'Zoho Books': {
      name: 'Zoho Books',
      type: 'OAUTH2',
      description: 'Automated invoice management and accounting',
      base_url: 'https://books.zoho.com/api/v3',
      placeholder_key: 'Enter your Zoho access token'
    }
  };

  const loadTemplate = (templateName) => {
    const template = integrationTemplates[templateName];
    setFormData({
      ...formData,
      name: template.name,
      type: template.type,
      description: template.description,
      base_url: template.base_url
    });
    setDialogOpen(true); // Open the dialog after loading template
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">API Integrations</h1>
          <p className="text-slate-600 mt-1">Connect with external services and automate workflows</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="modern-btn-primary bg-gradient-to-r from-lime-600 to-green-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Integration
            </Button>
          </DialogTrigger>
          <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>Add New Integration</DialogTitle>
              <DialogDescription>Connect your ERP with external services</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Quick Templates */}
              <div className="bg-lime-50 border border-lime-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-lime-800 mb-2">Quick Setup:</p>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => loadTemplate('GoFleet')}>
                    GoFleet Template
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => loadTemplate('Zoho Books')}>
                    Zoho Books Template
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Integration Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., GoFleet, Zoho Books"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="REST_API">REST API</SelectItem>
                      <SelectItem value="OAUTH2">OAuth 2.0</SelectItem>
                      <SelectItem value="WEBHOOK">Webhook</SelectItem>
                      <SelectItem value="CUSTOM">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this integration"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Base URL *</Label>
                <Input
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                  placeholder="https://api.example.com/v1"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder="Enter API key"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Secret</Label>
                  <Input
                    type="password"
                    value={formData.api_secret}
                    onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                    placeholder="Enter API secret"
                  />
                </div>
              </div>

              {formData.type === 'OAUTH2' && (
                <div className="space-y-2">
                  <Label>Access Token</Label>
                  <Input
                    type="password"
                    value={formData.access_token}
                    onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                    placeholder="Enter OAuth access token"
                  />
                </div>
              )}

              {formData.type === 'WEBHOOK' && (
                <div className="space-y-2">
                  <Label>Webhook URL</Label>
                  <Input
                    value={formData.webhook_url}
                    onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                    placeholder="Your webhook endpoint"
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-lime-600 border-gray-300 rounded focus:ring-lime-500"
                />
                <Label>Active (Enable this integration)</Label>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="modern-btn-primary bg-gradient-to-r from-lime-600 to-green-600">
                  Add Integration
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* GoFleet Integration Card */}
        <Card className="modern-card border-0">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg">
                <LinkIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl">GoFleet Integration</CardTitle>
                <CardDescription>Real-time motorcycle tracking</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Connect with GoFleet to track motorcycle locations in real-time, monitor driver behavior, and receive alerts.
            </p>
            <div className="space-y-2 text-sm">
              <p><strong>Features:</strong></p>
              <ul className="list-disc list-inside text-slate-600">
                <li>Real-time GPS tracking</li>
                <li>Geofence alerts</li>
                <li>Trip history and analytics</li>
                <li>Driver behavior monitoring</li>
              </ul>
            </div>
            <Button variant="outline" onClick={() => loadTemplate('GoFleet')} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Configure GoFleet
            </Button>
          </CardContent>
        </Card>

        {/* Zoho Books Integration Card */}
        <Card className="modern-card border-0">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg">
                <Code className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl">Zoho Books Integration</CardTitle>
                <CardDescription>Automated invoice management</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Sync invoices, expenses, and financial data with Zoho Books for seamless accounting.
            </p>
            <div className="space-y-2 text-sm">
              <p><strong>Features:</strong></p>
              <ul className="list-disc list-inside text-slate-600">
                <li>Automatic invoice creation</li>
                <li>Expense tracking</li>
                <li>Financial reports sync</li>
                <li>Customer management</li>
              </ul>
            </div>
            <Button variant="outline" onClick={() => loadTemplate('Zoho Books')} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Configure Zoho Books
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Active Integrations Table */}
      <Card className="modern-card border-0">
        <CardHeader>
          <CardTitle>Active Integrations</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Sync</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {integrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    No integrations configured yet. Add your first integration above.
                  </TableCell>
                </TableRow>
              ) : (
                integrations.map((integration) => (
                  <TableRow key={integration.id}>
                    <TableCell className="font-medium">{integration.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{integration.type}</Badge>
                    </TableCell>
                    <TableCell>
                      {integration.is_active ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {integration.last_sync ? formatDateTimeDisplay(integration.last_sync) : 'Never'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTest(integration.id)}
                          disabled={testingId === integration.id}
                        >
                          {testingId === integration.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(integration.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Integration Documentation */}
      <Card className="modern-card border-0 bg-gradient-to-r from-slate-50 to-gray-50">
        <CardHeader>
          <CardTitle>How to Add Custom Integrations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <h3 className="font-semibold mb-2">Step 1: Get API Credentials</h3>
            <p className="text-slate-600">
              Obtain API keys, secrets, or OAuth tokens from the service provider's developer portal.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Step 2: Configure Integration</h3>
            <p className="text-slate-600">
              Click "Add Integration", select the type (REST API, OAuth2, or Webhook), and enter your credentials.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Step 3: Test Connection</h3>
            <p className="text-slate-600">
              Use the test button to verify the integration is working correctly before activating it.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Webhook Endpoint</h3>
            <p className="text-slate-600 mb-2">
              To receive webhooks from external services, provide them with this URL:
            </p>
            <code className="block bg-slate-800 text-white p-3 rounded-lg">
              {API}/webhooks/[integration-name]
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Integrations;
